import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { EmptyState, FilterBar, PageHeader, StatusBadge } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useProReports, useProTemplates } from '../api'
import { PRO_STATUS_LABEL, type ProCaseStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

function startOfYear() {
  return `${new Date().getFullYear()}-01-01`
}

/**
 * The three questions anybody asks about PRO work.
 *
 * <p>How much is outstanding, where it gets stuck, and what it costs. The step-duration
 * report is the one that earns its keep: expected against actual is the only way anybody
 * finds out that one ministry counter has become the bottleneck.
 */
export function ProReportsPage() {
  useBreadcrumb()
  const templates = useProTemplates()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const [templateId, setTemplateId] = useState('')
  const [from, setFrom] = useState(startOfYear())
  const [to, setTo] = useState(todayLocal())

  const { byStatus, stepDurations, costs } = useProReports(from, to, templateId || undefined)

  return (
    <div className="space-y-6">
      <PageHeader
        title="PRO reports"
        subtitle="What is outstanding, where it gets stuck, and what it costs."
      />

      <FilterBar>
        <Select
          aria-label="Process"
          id="report-template" value={templateId}
          onChange={(event) => setTemplateId(event.target.value)}
          className="min-w-52"
        >
          <option value="">Every process</option>
          {(templates.data ?? []).map((template) => (
            <option key={template.id} value={template.id}>{template.name}</option>
          ))}
        </Select>

        <DatePicker
          aria-label="From" id="report-from" value={from} onChange={(e) => setFrom(e.target.value)} />

        <DatePicker
          aria-label="To" id="report-to" value={to} onChange={(e) => setTo(e.target.value)} />
      </FilterBar>

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-[var(--color-heading)]">Cases by status</h2>
        {byStatus.isPending && <Skeleton className="mt-3 h-24" />}
        {byStatus.data && (
          <div className="mt-3 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {byStatus.data.map((row) => (
              <div key={row.status}>
                <StatusBadge
                  status={row.status}
                  label={PRO_STATUS_LABEL[row.status as ProCaseStatus] ?? row.status}
                />
                <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
                  {row.cases}
                </p>
                {row.overdue > 0 && (
                  <p className="text-xs font-medium text-[var(--color-danger)]">
                    {row.overdue} overdue
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-[var(--color-heading)]">
          How long each step actually takes
        </h2>
        {stepDurations.isPending && <Skeleton className="mt-3 h-24" />}
        {stepDurations.data?.length === 0 && (
          <EmptyState
            title="No completed steps yet"
            description="Expected against actual appears once cases have moved through."
          />
        )}
        {(stepDurations.data?.length ?? 0) > 0 && (
          <div className="mt-3 overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-2 font-medium">Step</th>
                  <th scope="col" className="p-2 text-right font-medium">Expected</th>
                  <th scope="col" className="p-2 text-right font-medium">Average actual</th>
                  <th scope="col" className="p-2 text-right font-medium">Completed</th>
                  <th scope="col" className="p-2 text-right font-medium">Running</th>
                </tr>
              </thead>
              <tbody>
                {stepDurations.data?.map((row) => {
                  const over = row.averageActualDays != null
                    && row.expectedDays != null
                    && row.averageActualDays > row.expectedDays
                  return (
                    <tr key={row.stepName} className="border-b border-[var(--color-border)] last:border-0">
                      <td className="p-2 text-[var(--color-heading)]">{row.stepName}</td>
                      <td className="p-2 text-right tabular-nums">{row.expectedDays ?? '—'}</td>
                      <td className={`p-2 text-right tabular-nums${over ? ' font-medium text-[var(--color-danger)]' : ''}`}>
                        {row.averageActualDays == null ? 'not yet known' : row.averageActualDays.toFixed(1)}
                      </td>
                      <td className="p-2 text-right tabular-nums">{row.completedCount}</td>
                      <td className="p-2 text-right tabular-nums">{row.stillRunning}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-[var(--color-heading)]">Cost per case</h2>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Split by currency and never summed across them — a total over dirhams and rupees
          looks like money and is not.
        </p>
        {costs.isPending && <Skeleton className="mt-3 h-24" />}
        {costs.data?.length === 0 && (
          <EmptyState title="No costs recorded" description="Nothing was spent on cases opened in this period." />
        )}
        {(costs.data?.length ?? 0) > 0 && (
          <div className="mt-3 overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-2 font-medium">Entity</th>
                  <th scope="col" className="p-2 font-medium">Currency</th>
                  <th scope="col" className="p-2 text-right font-medium">Cases</th>
                  <th scope="col" className="p-2 text-right font-medium">Total</th>
                  <th scope="col" className="p-2 text-right font-medium">Average</th>
                </tr>
              </thead>
              <tbody>
                {costs.data?.map((row) => (
                  <tr key={`${row.legalEntityId}-${row.currency}`} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-2 text-[var(--color-heading)]">
                      {entities.data?.find((e) => e.id === row.legalEntityId)?.name ?? '—'}
                    </td>
                    <td className="p-2">{row.currency}</td>
                    <td className="p-2 text-right tabular-nums">{row.cases}</td>
                    <td className="p-2 text-right tabular-nums">{row.total.toFixed(2)}</td>
                    <td className="p-2 text-right tabular-nums">{row.averagePerCase.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
